import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, FileText, Folder } from 'lucide-react';

function folderOverviewSlug(node) {
  const overview = node.children?.find(
    (child) => child.type === 'page' && child.segment === 'overview',
  );
  return overview?.slug ?? null;
}

function TreeItem({ node, depth, activeSlug }) {
  const overviewSlug = node.type === 'folder' ? folderOverviewSlug(node) : null;
  const isOnOverview = overviewSlug && activeSlug === overviewSlug;

  const [open, setOpen] = useState(() => {
    if (node.type === 'page') return false;
    return activeSlug.startsWith(`${node.slug}/`) || activeSlug === node.slug;
  });

  useEffect(() => {
    if (node.type === 'page') return;
    if (activeSlug.startsWith(`${node.slug}/`) || activeSlug === node.slug) {
      setOpen(true);
    }
  }, [activeSlug, node.slug, node.type]);

  const isActive = node.type === 'page' && activeSlug === node.slug;
  const paddingLeft = 8 + depth * 12;

  if (node.type === 'folder') {
    return (
      <div>
        <div
          className="flex w-full items-center rounded-md hover:bg-gray-100"
          style={{ paddingLeft }}
        >
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex shrink-0 items-center justify-center p-1.5 text-gray-500 hover:text-gray-700"
            aria-label={open ? 'Collapse section' : 'Expand section'}
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {overviewSlug ? (
            <Link
              to={`/wiki/${overviewSlug}`}
              className={`flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pr-2 text-sm ${
                isOnOverview
                  ? 'font-medium text-blue-700'
                  : 'font-medium text-gray-700'
              }`}
            >
              <Folder className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="truncate">{node.title}</span>
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 pr-2 text-left text-sm font-medium text-gray-700"
            >
              <Folder className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="truncate">{node.title}</span>
            </button>
          )}
        </div>
        {open &&
          node.children.map((child) => (
            <TreeItem
              key={`${child.type}-${child.slug}`}
              node={child}
              depth={depth + 1}
              activeSlug={activeSlug}
            />
          ))}
      </div>
    );
  }

  return (
    <Link
      to={`/wiki/${node.slug}`}
      className={`flex items-center gap-1.5 rounded-md py-1.5 pr-2 text-sm hover:bg-gray-100 ${
        isActive ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
      }`}
      style={{ paddingLeft: paddingLeft + 20 }}
    >
      <FileText className="h-4 w-4 shrink-0 opacity-60" />
      <span className="truncate">{node.title}</span>
    </Link>
  );
}

export function WikiTree({ tree, activeSlug }) {
  return (
    <nav className="space-y-0.5 py-2">
      {tree.map((node) => (
        <TreeItem
          key={`${node.type}-${node.slug}`}
          node={node}
          depth={0}
          activeSlug={activeSlug}
        />
      ))}
    </nav>
  );
}
